import React, { useEffect, useState } from "react";
import axiosInstance from "../axios/axios";
import jobMenu from "../assets/jobMenu.svg";
import whiteAppIcon from "../assets/whiteAppIcon.svg";
import closedIcon from "../assets/closedIcon.svg";
import expiredIcon from "../assets/expiredIcon.svg";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import DeleteRequest from "./DeleteRequest";
import ErrorMessage from "./ErrorMessage";
import SuccessMessage from "./SuccessMessage";

const CompanyJobs = () => {
  const filters = ["Active", "Closed", "Expired"];
  const [activeFilter, setActiveFilter] = useState(filters[0]);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteJobId, setDeleteJobId] = useState(null);
  const [activeJobMenu, setActiveJobMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const org_id = localStorage.getItem("role_specific_id");
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanyJobs();
  }, [org_id]);

  const fetchCompanyJobs = async () => {
    if (!org_id) {
      setError("Organization ID not found.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await axiosInstance.get("jobs");
      const currentDate = new Date();
      const foundJobs = res.data.jobs.data
        .filter((job) => job.org_id === parseInt(org_id))
        .map((job) => {
          const isExpired = job.deadline
            ? new Date(job.deadline) < currentDate
            : false;
          return {
            ...job,
            status: isExpired
              ? "Expired"
              : job.status === 1
              ? "Active"
              : "Closed",
          };
        });

      setJobs(foundJobs);
    } catch (error) {
      console.error(error);
      setError("Failed to fetch jobs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter((job) => {
    if (activeFilter === "Active") return job.status === "Active";
    if (activeFilter === "Closed") return job.status === "Closed";
    return job.status === "Expired";
  });

  const calculateDaysAgo = (createdAt) => {
    const createdDate = new Date(createdAt);
    const today = new Date();
    
    today.setHours(0, 0, 0, 0);
    createdDate.setHours(0, 0, 0, 0);
    
    const timeDifference = today - createdDate;
    const daysAgo = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    
    if (daysAgo === 0) return "Today";
    if (daysAgo === 1) return "Yesterday";
    if (daysAgo > 0) return `${daysAgo} days ago`;
    return `In ${Math.abs(daysAgo)} days`;
  };

  const handleDeleteJobs = (id) => {
    setDeleteJobId(id);
    setOpenDelete(true);
    setActiveJobMenu(null);
  };

  const handleEditJobs = (id) => {
    navigate(`/edit-job/${id}`);
    setActiveJobMenu(null);
  };

  const toggleJobMenu = (id) => {
    setActiveJobMenu(prevMenu => prevMenu === id ? null : id);
  };

  const closeJob = async (id) => {
    try {
      const response = await axiosInstance.put(
        `closeORopenJOB/${id}/toggle-status`,
        { status: 0 }
      );

      if (response.status === 200) {
        setJobs(prevJobs =>
          prevJobs.map(job =>
            job.id === id ? { ...job, status: "Closed" } : job
          )
        );
        setSuccess("Job closed successfully");
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      setError("Failed to close job");
      setTimeout(() => setError(null), 3000);
    } finally {
      setActiveJobMenu(null);
    }
  };

  const activateJob = async (id) => {
    try {
      const response = await axiosInstance.put(
        `closeORopenJOB/${id}/toggle-status`,
        { status: 1 }
      );

      if (response.status === 200) {
        setJobs(prevJobs =>
          prevJobs.map(job =>
            job.id === id ? { ...job, status: "Active" } : job
          )
        );
        setSuccess("Job activated successfully");
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      setError("Failed to activate job");
      setTimeout(() => setError(null), 3000);
    } finally {
      setActiveJobMenu(null);
    }
  };

  const truncateText = (text, maxLength) => {
    return text?.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const getJobCount = (status) => {
    return jobs.filter(job => job.status === status).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 sm:px-6 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="flex gap-4 mb-6">
              {[1,2,3].map(i => (
                <div key={i} className="h-10 bg-gray-200 rounded w-20"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl border p-6">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      {/* <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Job Listings</h1>
              <p className="text-gray-600 mt-1">Manage and track your job postings</p>
            </div>
            <Link
              to="/create-job"
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Create Job</span>
              <span className="sm:hidden">Create</span>
            </Link>
          </div>
        </div>
      </div> */}

      <div className="px-4 sm:px-6 py-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          {filters.map((filter) => (
            <div key={filter} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{filter} Jobs</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {getJobCount(filter)}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${
                  filter === "Active" ? "bg-green-100" : 
                  filter === "Closed" ? "bg-yellow-100" : "bg-red-100"
                }`}>
                  {filter === "Active" && (
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {filter === "Closed" && (
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {filter === "Expired" && (
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            {filters.map((filter) => (
              <button
                key={filter}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  activeFilter === filter
                    ? "bg-white text-green-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setActiveFilter(filter)}
              >
                {filter} ({getJobCount(filter)})
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        {error && <ErrorMessage message={error} />}
        {success && <SuccessMessage message={success} />}

        {/* Jobs Grid */}
        {filteredJobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeFilter.toLowerCase()} jobs found</h3>
            <p className="text-gray-600 mb-6">
              {activeFilter === "Active" 
                ? "Create your first job posting to start receiving applications." 
                : `You don't have any ${activeFilter.toLowerCase()} jobs at the moment.`
              }
            </p>
            {activeFilter === "Active" && (
              <Link
                to="/dashboard/create-job"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Your First Job
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300"
              >
                {/* Job Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
                      {job.job_title}
                    </h3>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        job.status === "Active" 
                          ? "bg-green-50 text-green-700 border border-green-200" 
                          : job.status === "Closed"
                          ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                          : "bg-red-50 text-red-700 border border-red-200"
                      }`}>
                        {job.status === "Active" && (
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                        {job.status === "Closed" && (
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v4a1 1 0 11-2 0V7zM9 13a1 1 0 112 0 1 1 0 01-2 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {job.status === "Expired" && (
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                        {job.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        Posted {calculateDaysAgo(job.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions Menu */}
                  <div className="relative ml-4">
                    <button
                      onClick={() => toggleJobMenu(job.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <img src={jobMenu} className="w-5 h-5" alt="Menu" />
                    </button>
                    
                    {activeJobMenu === job.id && (
                      <div className="absolute right-0 top-10 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[150px]">
                        {job.status !== "Active" && (
                          <button
                            onClick={() => activateJob(job.id)}
                            className="w-full px-4 py-2 text-left text-green-600 hover:bg-gray-50 flex items-center text-sm"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Activate
                          </button>
                        )}
                        {job.status !== "Closed" && (
                          <button
                            onClick={() => closeJob(job.id)}
                            className="w-full px-4 py-2 text-left text-yellow-600 hover:bg-gray-50 flex items-center text-sm"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Close Job
                          </button>
                        )}
                        <button
                          onClick={() => handleEditJobs(job.id)}
                          className="w-full px-4 py-2 text-left text-blue-600 hover:bg-gray-50 flex items-center text-sm"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit Job
                        </button>
                        <button
                          onClick={() => handleDeleteJobs(job.id)}
                          className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-50 flex items-center text-sm"
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Job Description */}
                <p className="text-gray-600 mb-6 leading-relaxed text-sm line-clamp-3">
                  {truncateText(job.job_description, 150)}
                </p>

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  {job.status === "Active" && (
                    <Link
                      to={`${org_id}/${job.id}`}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
                    >
                      <img src={whiteAppIcon} className="w-4 h-4 mr-2" alt="" />
                      View Applications
                    </Link>
                  )}
                  
                  {job.status === "Closed" && (
                    <div className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-lg">
                      <img src={closedIcon} className="w-4 h-4 mr-2" alt="" />
                      Closed
                    </div>
                  )}
                  
                  {job.status === "Expired" && (
                    <div className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-100 text-red-800 text-sm font-medium rounded-lg">
                      <img src={expiredIcon} className="w-4 h-4 mr-2" alt="" />
                      Expired
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Modal */}
        {openDelete && (
          <DeleteRequest 
            id={deleteJobId} 
            setOpenDelete={setOpenDelete}
            onSuccess={() => {
              setJobs(prevJobs => prevJobs.filter(job => job.id !== deleteJobId));
              setSuccess("Job deleted successfully");
              setTimeout(() => setSuccess(null), 3000);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CompanyJobs;